import { z } from "zod";

export enum FilterType {
  FILTER = "filter",
  COLUMN_PICKER = "column_picker",
  OPTIN_FEATURES = "optin_features",
}

export enum FilterApplicableFor {
  WAREHOUSE_QUERIES = "warehouse_queries",
  DBT_MODELS = "dbt_models",
  DBT_JOB_MODELS = "dbt_job_models",
  DBT_GOVERNANCE_CONFIGS = "dbt_governance_configs",
  AUTOTUNE_JOBS = "autotune_jobs",
  JOBS = "jobs",
  QUERY_GROUP = "query_group",
  GOVERNANCE_ROLES = "governance_roles",
  GOVERNANCE_USERS = "governance_users",
  WAREHOUSES = "warehouses",
  DATASET_LIST = "datasetslist",
  DATASETS = "datasets",
  QUERY = "query",
  DISCOVER = "discover",
  BI_REPORTS = "bi_reports",
  BI_REPORTS_USERS = "bi_reports_users",
  MCP_MEMORIES = "mcp_memories",
  TABLE_QUERIES = "table_queries",
  TAGS = "custom_tags",
  SUBSCRIPTIONS = "subscriptions",
  STORED_PROCEDURES = "stored_procedures",
  TASKS = "tasks",
  SNOWFLAKE_WORKLOADS = "snowflake_workloads",
  TABLEAU_WORKLOADS = "tableau_workloads",
  DBT_JOBS = "dbt_jobs",
  DBT_JOB_RUNS = "dbt_job_runs",
  AI_SERVICE_USAGE = "ai_service_usage",
  CHECKS = "checks",
  DATABRICKS_QUERIES = "databricks_queries",
  DATABRICKS_USERS = "databricks_users",
  DATABRICKS_JOB_RUNS = "databricks_job_runs",
  CLUSTERS = "clusters",
  SQL_WAREHOUSES = "sql_warehouses",
  AI_ML_ENDPOINTS = "ai_ml_endpoints",
  DASH_WAREHOUSES = "dash_warehouses",
  DASH_JOBS = "dash_jobs",
  DASH_CLUSTERS = "dash_clusters",
  DASH_QUERIES = "dash_queries",
  DASH_SQL_WAREHOUSES = "dash_sql_warehouses",
  DASH_USERS = "dash_users",
  DASH_AI_ML = "dash_ai_ml",
  DASH_AI_ML_MODEL_SERVING = "dash_ai_ml_model_serving",
  DASH_AI_ML_VECTOR_SEARCH = "dash_ai_ml_vector_search",
  DASH_AI_ML_AI_GATEWAY = "dash_ai_ml_ai_gateway",
  DASH_STORED_PROCEDURES = "dash_stored_procedures",
  DASH_SIGMA_WORKBOOKS = "dash_sigma_workbooks",
  // The v2 AI Services list table. Deliberately its own bucket rather than
  // reusing the legacy `AI_SERVICE_USAGE` scope below — that one backs v1's
  // DataTable, with its own saved filters/column choices, and sharing it
  // would let a v1 column selection hide columns on this unrelated table.
  DASH_AI_SERVICES = "dash_ai_services",
  DASH_AI_SERVICE_USAGE = "dash_ai_service_usage",
  // The two drill-downs, each its own bucket: they list different populations
  // with different columns, so sharing a scope would let one table's saved
  // column choice hide columns on the other. A scope is ALSO what gates the
  // More Filters button — `AddFiltersButton` renders it only when
  // `applicableFor` is set, and `DataTable` suppresses per-column header
  // filters whenever a filter portal exists. So a table with no scope has no
  // way at all to reach a filter its dataset declares.
  DASH_STORED_PROCEDURE_RUNS = "dash_stored_procedure_runs",
  DASH_STORED_PROCEDURE_CHILD_QUERIES = "dash_stored_procedure_child_queries",
  DASH_TEAM_SPENDS = "dash_team_spends",
  DASH_DATASETS_USAGE = "dash_datasets_usage",
  DASH_ALL_QUERY = "dash_all_query",
  DASH_QUERY_GROUPS = "dash_query_groups",
  DASH_QUERY_GROUP_QUERIES = "dash_query_group_queries",
  DASH_WORKLOADS = "dash_workloads",
  DASH_WORKLOAD_TASKS = "dash_workload_tasks",
  DASH_TASKS = "dash_tasks",
  DASH_TASK_RUNS = "dash_task_runs",
  DASH_WORKLOAD_RUNS = "dash_workload_runs",
  DASH_WORKLOAD_RUN_QUERIES = "dash_workload_run_queries",
  DASH_DBT_RUN_QUERIES = "dash_dbt_run_queries",
  DASH_WORKLOAD_RUN_COMPARE = "dash_workload_run_compare",
  DASH_WORKLOAD_TASK_RUNS = "dash_workload_task_runs",
  DASH_NOTEBOOKS = "dash_notebooks",
  DASH_STREAMLIT_APPS = "dash_streamlit_apps",
  DASH_STREAMLIT_APP_RUNS = "dash_streamlit_app_runs",
  DASH_STREAMLIT_CHILD_QUERIES = "dash_streamlit_child_queries",
  // Cost Breakdown's two breakdown tabs, each its own bucket (same rationale as
  // the drill-downs above): they list different populations with different
  // columns, and a scope is what gates each table's SKU / Product filter.
  DASH_COST_BY_SKU = "dash_cost_by_sku",
  DASH_COST_BY_PRODUCT = "dash_cost_by_product",
}

export interface Filter {
  filterData?: Record<string, unknown>;
  filterStr: string | string[];
  label: string;
  onclose?: (removedFilter?: string) => void;
  searchKey?: string;
  bgColor?: string;
  condition?: string;
  valueLabels?: Record<string, string>;
}

export const savedFilterSchema = z.object({
  name: z
    .string()
    .max(100, { message: "Name must not exceed 100 characters" })
    .min(1, { message: "Name is required" }),
  data: z.record(z.string(), z.unknown()).refine(data => Object.keys(data).length > 0, {
    message: "At least one filter must be selected",
  }),
  applicable_for: z.enum(Object.values(FilterApplicableFor) as [string, ...string[]]),
  type: z.enum(Object.values(FilterType) as [string, ...string[]]),
});

export type SavedFilter = z.infer<typeof savedFilterSchema> & { id: string };

export type FilterValue = { values: string | string[]; condition: string };

// Schema for preset/filter API responses
export const PresetIdResponseSchema = z.object({
  preset_id: z.number(),
});
